import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchCommitment, deleteCommitment, updateCommitment } from './api';

const CommitmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [commitment, setCommitment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    commitment_name: '',
    commitment_description: '',
    number_of_calls: '',
    note: '',
    commitment_status: false,
  });
  const [isSuperuser, setIsSuperuser] = useState(localStorage.getItem('is_superuser') || 'false');

  // Helper function to check if a commitment was created today
  const isCommitmentFromToday = (createdAt) => {
    const today = new Date();
    const commitmentDate = new Date(createdAt);

    return (
      today.getFullYear() === commitmentDate.getFullYear() &&
      today.getMonth() === commitmentDate.getMonth() &&
      today.getDate() === commitmentDate.getDate()
    );
  };

  useEffect(() => {
    const getCommitment = async () => {
      try {
        setIsLoading(true);
        const response = await fetchCommitment(id);
        setCommitment(response.data);
        setError(null);
      } catch (error) {
        console.error('Error fetching commitment:', error);
        setError('Failed to load commitment details');
      } finally {
        setIsLoading(false);
      }
    };

    getCommitment();
  }, [id]);

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this commitment?')) {
      try {
        await deleteCommitment(id);
        navigate('/CommitmentList');
      } catch (error) {
        console.error('Error deleting commitment:', error);
        setError('Failed to delete commitment');
      }
    }
  };

  const openEditModal = () => {
    // Prefill the form with current commitment data
    setEditFormData({
      commitment_name: commitment.commitment_name,
      commitment_description: commitment.commitment_description || '',
      number_of_calls: commitment.number_of_calls || '',
      note: commitment.note || '',
      commitment_status: commitment.commitment_status,
    });
    setIsEditModalOpen(true);
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateCommitment(id, editFormData);
      // Update the local commitment state
      setCommitment({
        ...commitment,
        ...editFormData,
      });
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Error updating commitment:', error);
      setError('Failed to update commitment');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto mt-10 p-6 bg-red-50 border border-red-200 rounded-lg">
        <h2 className="text-xl font-bold text-red-700 mb-4">Error</h2>
        <p className="text-red-600">{error}</p>
        <button
          onClick={() => navigate('/CommitmentList')}
          className="mt-4 w-full bg-red-500 text-white py-2 rounded-md hover:bg-red-600"
        >
          Return to Commitments
        </button>
      </div>
    );
  }

  if (!commitment) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md my-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                Edit Commitment
              </h2>
            </div>
            <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="commitment_name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Commitment Name
                  </label>
                  <input
                    type="text"
                    id="commitment_name"
                    name="commitment_name"
                    value={editFormData.commitment_name}
                    onChange={handleEditChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label
                    htmlFor="commitment_description"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Description
                  </label>
                  <textarea
                    id="commitment_description"
                    name="commitment_description"
                    value={editFormData.commitment_description}
                    onChange={handleEditChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label
                    htmlFor="number_of_calls"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Connected Calls
                  </label>
                  <input
                    type="number"
                    id="number_of_calls"
                    name="number_of_calls"
                    value={editFormData.number_of_calls}
                    onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label
                    htmlFor="note"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Additional Notes
                  </label>
                  <textarea
                    id="note"
                    name="note"
                    value={editFormData.note}
                    onChange={handleEditChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="commitment_status"
                    name="commitment_status"
                    checked={editFormData.commitment_status}
                    onChange={handleEditChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="commitment_status"
                    className="ml-2 block text-sm text-gray-900"
                  >
                    Completed
                  </label>
                </div>

                <div className="flex justify-end space-x-4 mt-6 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Existing Detail View */}
      <div className="max-w-2xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gray-100 p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-3xl font-bold text-gray-800">
              {commitment.commitment_name}
            </h2>
            <span
              className={`px-3 py-1 rounded-full text-sm font-semibold ${commitment.commitment_status
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
                }`}
            >
              {commitment.commitment_status ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        {/* Details */}
        <div className="p-6 space-y-4">
          {commitment.commitment_description && (
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Description</h3>
              <p className="text-gray-600">
                {commitment.commitment_description || 'No description provided'}
              </p>
            </div>
          )}

          {commitment.number_of_calls !== null && commitment.number_of_calls !== undefined && (
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Connected Calls</h3>
              <p className="text-gray-600">
                {commitment.number_of_calls}
              </p>
            </div>
          )}

          {commitment.note && (
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Additional Notes</h3>
              <p className="text-gray-600">
                {commitment.note || 'No additional notes'}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="bg-gray-50 p-6 border-t border-gray-200">
          {isCommitmentFromToday(commitment.created_at) && isSuperuser === 'true' ? (
            <div className="flex justify-between items-center">
              <button
                onClick={openEditModal}
                className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-300"
              >
                Edit Commitment
              </button>
              <button
                onClick={handleDelete}
                className="bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition duration-300"
              >
                Delete Commitment
              </button>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-gray-600 text-sm">
                {!isCommitmentFromToday(commitment.created_at)
                  ? 'This commitment was created on a previous day and cannot be edited.'
                  : 'Only administrators can edit or delete commitments.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Back to List */}
      <div className="text-center mt-6">
        <Link
          to="/CommitmentList"
          className="text-blue-500 hover:text-blue-700 hover:underline"
        >
          ← Back to Commitments List
        </Link>
      </div>
    </div>
  );
};

export default CommitmentDetail;